import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  CampaignListResponse,
  CampaignListRow,
  CampaignSource,
  PeriodRollup,
  StoreResponse,
} from '@clarity/contract';
import { useCampaigns, useStoreBannerOptions } from '../../queries/index.js';
import { useScopeContext } from '../../lib/scopeContext.js';
import { useI18n } from '../../lib/i18n.js';
import { isNotYetOnboarded } from '../../lib/lifecycle.js';
import { isOnboardingRequired, money, num, pct } from '../format.js';
import { discountRangeLabel } from './occurrence-shared.js';
import { IconCampaigns } from '../../components/icons.js';
import { TermsView } from './TermsView.js';
import { BannersView, MegaphoneIcon } from './BannersView.js';

/**
 * The Campaigns list surface (clarity-campaign-matching task 13.1, Req 9.1, 9.2),
 * rebuilt from the agreed mock `brand/campaigns-mock.html` in the teal-glass
 * world. The mock is the contract: the headline strip, the source tabs (a deduped
 * campaign shows multiple source dots + "Monthly + Platform"), and the
 * actual-vs-projected table are reproduced here as real components over the typed
 * ts-rest client, with the real tokens/classes (`--font-num` tabular figures,
 * logical properties for RTL, WCAG 2.1 AA landmarks + `aria-current` + focus).
 *
 * Campaigns is a FLEXIBLE surface in the registry (valid at all/brand/store
 * scope), but the list read (`GET /stores/:id/campaigns`) is PER STORE. For this
 * task — the surface registration + the list view + the query hooks — the list
 * renders for the scope's SINGLE branch (a store scope, or a one-branch
 * all/brand that resolves to a single store). A multi-branch all/brand scope
 * prompts the user to pick a branch, mirroring the branch-only surfaces: the
 * per-store campaigns detail (tasks 13.2+) is reached one branch at a time, and a
 * combined cross-branch campaigns view is not in scope here.
 */
export function CampaignsList() {
  const { scope, branches, store, isSingleBranch } = useScopeContext();

  // A store scope resolves `store`; a one-branch all/brand resolves to its lone
  // branch. Either way, render the per-store list for that single branch.
  const only =
    store ?? (isSingleBranch && branches.length === 1 ? (branches[0] as StoreResponse) : null);

  if (only) {
    return (
      <section data-surface="campaigns" data-scope={scope.kind}>
        <StoreCampaignsList store={only} />
      </section>
    );
  }

  return (
    <section data-surface="campaigns" data-scope={scope.kind}>
      <PickBranchForCampaigns branches={branches} />
    </section>
  );
}

/**
 * The list view for ONE store. Reads `useCampaigns(storeId)` and renders the
 * mock's three regions: the headline strip, the source tabs, and the campaign
 * table — with real EMPTY (no campaigns / pre-match) and LOADING (query pending)
 * states. Exported so a later combined/per-branch host can mount it directly and
 * the tests can drive it from a fake ts-rest client.
 *
 * Wraps the campaigns table + the new Terms view (clarity-campaign-terms-economics
 * task 6.2) in the top-level `Campaigns | Terms` switch (`.vswitch` from the mock
 * `brand/campaigns-terms-mock.html`). Campaigns is the DEFAULT tab and renders the
 * existing table exactly as before; Terms renders {@link TermsView}. The switch is
 * a `role=tablist` with `aria-selected` (keyboard-operable, WCAG 2.1 AA).
 *
 * A third tab, "Banners you can join" (clarity-banner-options Req 8.3), renders
 * {@link BannersView}; when the store has New banner options the Campaigns view
 * leads with a notice that opens it (Req 8.2).
 */
export function StoreCampaignsList({ store }: { store: StoreResponse }) {
  const { t } = useI18n();
  const query = useCampaigns(store.id, !isNotYetOnboarded(store));
  const onboardingBlocked = isOnboardingRequired(query.error);
  const [view, setView] = useState<'campaigns' | 'terms' | 'banners'>('campaigns');
  const bannersTabRef = useRef<HTMLButtonElement>(null);

  const data: CampaignListResponse | null =
    query.data?.status === 200 ? query.data.body : null;
  // The Campaigns tab count: the loaded campaign count once known (else blank,
  // not a fabricated zero while pending). Terms has no cheap count without its
  // own fetch, so its badge is omitted until the tab is opened.
  const campaignsCount = data ? data.campaigns.length : null;

  // Banner options (clarity-banner-options Req 8.2, 8.3): the same query the
  // Banners tab reads (deduped by key), so the tab count and the new-options
  // notice are known before the tab is opened.
  const banners = useStoreBannerOptions(store.id);
  const bannersBody = banners.data?.status === 200 ? banners.data.body : null;
  const bannersCount = bannersBody ? bannersBody.options.length : null;
  const newCount = bannersBody?.newCount ?? 0;
  const platform = t(`bannerOptions.platform.${store.platformKey}`);

  const openBanners = () => {
    setView('banners');
    bannersTabRef.current?.focus();
  };

  return (
    <div className="campaigns-surface">
      <div className="vswitch" role="tablist" aria-label="View">
        <button
          type="button"
          role="tab"
          id="campaigns-view-tab-campaigns"
          aria-selected={view === 'campaigns'}
          aria-controls="campaigns-view-panel"
          className="vswitch__tab"
          onClick={() => setView('campaigns')}
        >
          <IconCampaigns className="vswitch__ico" aria-hidden="true" />
          Campaigns
          {campaignsCount !== null && (
            <span className="vswitch__cnt num">{campaignsCount}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          id="campaigns-view-tab-terms"
          aria-selected={view === 'terms'}
          aria-controls="campaigns-view-panel"
          className="vswitch__tab"
          onClick={() => setView('terms')}
        >
          <IconTerms className="vswitch__ico" aria-hidden="true" />
          Terms
        </button>
        <button
          ref={bannersTabRef}
          type="button"
          role="tab"
          id="campaigns-view-tab-banners"
          aria-selected={view === 'banners'}
          aria-controls="campaigns-view-panel"
          className="vswitch__tab"
          onClick={() => setView('banners')}
        >
          <MegaphoneIcon className="vswitch__ico" />
          {t('banners.tab')}
          {bannersCount !== null && <span className="vswitch__cnt num">{bannersCount}</span>}
        </button>
      </div>

      <div
        id="campaigns-view-panel"
        role="tabpanel"
        aria-labelledby={`campaigns-view-tab-${view}`}
      >
        {view === 'campaigns' && newCount > 0 && (
          <div className="banners-view__notice">
            <MegaphoneIcon className="banners-view__i" />
            <span>
              <b>
                {t(newCount === 1 ? 'banners.notice.count.one' : 'banners.notice.count.many', {
                  count: newCount,
                })}
              </b>{' '}
              {t('banners.notice.on', { platform })}
            </span>
            <button type="button" className="ghost" onClick={openBanners}>
              {t('banners.notice.see')}
            </button>
          </div>
        )}
        {view === 'banners' ? (
          <BannersView store={store} />
        ) : view === 'terms' ? (
          <TermsView store={store} />
        ) : query.isLoading ? (
          <CampaignsLoading />
        ) : // A `409` is the expected not-yet-onboarded block (the nav lock normally
        // keeps the surface off-limits); surface it as the pre-match empty state.
        query.isError && !onboardingBlocked ? (
          <CampaignsError />
        ) : !data || data.campaigns.length === 0 ? (
          <CampaignsEmpty storeName={store.name ?? store.externalStoreId} />
        ) : (
          <CampaignsTable store={store} data={data} />
        )}
      </div>
    </div>
  );
}

/** The Terms switch-tab icon (a ledger-rows glyph), matching the mock's `terms`
 *  icon; local to this surface so it does not crowd the shared icon set. */
function IconTerms({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 7h16M4 12h16M4 17h10" />
      <circle cx="18.5" cy="17" r="2.2" />
    </svg>
  );
}

// ── The loaded list ───────────────────────────────────────────────────────────

/** The four source tabs, in the mock's order. `all` has no dot. */
const SOURCE_TABS: readonly ('all' | CampaignSource)[] = [
  'all',
  'monthly',
  'ads',
  'platform',
];

const SOURCE_LABEL: Record<CampaignSource, string> = {
  monthly: 'Monthly',
  ads: 'Ads',
  platform: 'Platform',
};

const currency = (store: StoreResponse) => store.profile?.currency ?? 'JOD';

function CampaignsTable({ store, data }: { store: StoreResponse; data: CampaignListResponse }) {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [active, setActive] = useState<'all' | CampaignSource>('all');
  const cur = currency(store);

  // Per-source counts for the tab badges — a deduped campaign (sources.length>1)
  // counts under each of its sources, so the per-source counts can exceed `all`.
  const counts = useMemo(() => {
    const c: Record<'all' | CampaignSource, number> = {
      all: data.campaigns.length,
      monthly: 0,
      ads: 0,
      platform: 0,
    };
    for (const row of data.campaigns) {
      for (const s of row.campaign.sources) c[s] += 1;
    }
    return c;
  }, [data.campaigns]);

  const rows = useMemo(
    () =>
      active === 'all'
        ? data.campaigns
        : data.campaigns.filter((row) => row.campaign.sources.includes(active)),
    [active, data.campaigns],
  );

  // The campaigns detail route (task 13.2, `CampaignDetail`) is wired as a
  // sibling of the `campaigns` surface under the store scope; this row open
  // navigates there.
  const openCampaign = (campaignId: string) => {
    if (!clientId) return;
    navigate(`/c/${clientId}/store/${store.id}/campaigns/${campaignId}`);
  };

  return (
    <div className="campaigns">
      <div className="page-head">
        <div>
          <h2>Campaigns</h2>
          <p className="campaigns__lede">
            Every campaign running on this branch, matched to the orders it drove — so you
            can see what each one actually cost and earned, projected against what we
            expected.
          </p>
        </div>
      </div>

      <HeadlineStrip rollup={data.rollup} currency={cur} />

      <div className="campaigns__tabs" role="tablist" aria-label="Campaign source">
        {SOURCE_TABS.map((src) => (
          <button
            key={src}
            type="button"
            role="tab"
            id={`campaign-tab-${src}`}
            aria-selected={active === src}
            aria-controls="campaign-rows"
            className="campaigns__tab"
            onClick={() => setActive(src)}
          >
            {src !== 'all' && (
              <span className={`src-dot src-${src}`} aria-hidden="true" />
            )}
            {src === 'all' ? 'All sources' : SOURCE_LABEL[src]}
            <span className="campaigns__tab-count num">{counts[src]}</span>
          </button>
        ))}
      </div>

      <div className="card campaigns__tablewrap">
        <table
          className="campaigns__table"
          id="campaign-rows"
          aria-label="Campaigns"
          aria-live="polite"
        >
          <thead>
            <tr>
              <th scope="col">Campaign</th>
              <th scope="col">Source</th>
              <th scope="col">Status</th>
              <th scope="col">Matched orders</th>
              <th scope="col">Actual vs projected sales</th>
              <th scope="col" className="num-col">
                Cost
              </th>
              <th scope="col" className="num-col">
                Net impact
              </th>
              <th scope="col" className="num-col">
                ROAS
              </th>
              <th scope="col">
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <CampaignRow
                key={row.campaign.id}
                row={row}
                currency={cur}
                onOpen={() => openCampaign(row.campaign.id)}
              />
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="campaigns__no-match muted" role="status">
            No campaigns from this source in the period.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * The headline strip (Req 9.2): orders matched / total, campaign-driven sales,
 * total ad+promo cost (with the "ties to payout ads · Δ" line from `adsSpendTie`),
 * and blended ROAS. Figures use `--font-num` tabular via `.num`/`.stat__value`.
 */
function HeadlineStrip({ rollup, currency }: { rollup: PeriodRollup; currency: string }) {
  const matchedPct =
    rollup.ordersTotal > 0 ? (rollup.ordersMatched / rollup.ordersTotal) * 100 : null;
  const unmatched = Math.max(0, rollup.ordersTotal - rollup.ordersMatched);
  const tie = rollup.adsSpendTie;

  return (
    <div className="campaigns__summary">
      <div className="card stat">
        <h4 className="stat__label">Orders matched</h4>
        <div className="stat__value">
          {num(rollup.ordersMatched)}
          <span className="unit">/ {num(rollup.ordersTotal)}</span>
        </div>
        <div className="stat__sub">
          {matchedPct === null ? 'no orders in period' : `${pct(round1(matchedPct))} of all orders`}
        </div>
        <div className="stat__hint">
          {num(unmatched)} order{unmatched === 1 ? '' : 's'} matched no campaign
        </div>
      </div>

      <div className="card stat">
        <h4 className="stat__label">Campaign-driven sales</h4>
        <div className="stat__value">
          {num(round0(rollup.campaignDrivenSales))}
          <span className="unit">{currency}</span>
        </div>
        <div className="stat__sub">from the matched orders</div>
      </div>

      <div className="card stat">
        <h4 className="stat__label">Total ad + promo cost</h4>
        <div className="stat__value">
          {num(round0(rollup.totalAdPromoCost))}
          <span className="unit">{currency}</span>
        </div>
        <div className="stat__sub">
          {money(rollup.adsSpend, currency)} ads · {money(rollup.promoCost, currency)} promo
        </div>
        {tie && (
          <div className={`stat__hint${tie.withinTolerance ? '' : ' campaigns__tie--off'}`}>
            ties to payout ads {money(tie.payoutMarketingAds, currency)} · Δ{' '}
            {money(tie.delta, currency)}
          </div>
        )}
      </div>

      <div className="card stat">
        <h4 className="stat__label">Blended ROAS</h4>
        <div className="stat__value">
          {rollup.blendedRoas === null ? '—' : num(round2(rollup.blendedRoas))}
          {rollup.blendedRoas !== null && <span className="unit">×</span>}
        </div>
        <div className="stat__sub">return on ad + promo spend</div>
      </div>
    </div>
  );
}

/** One campaign row: name + Arabic + terms, source dots, status, matched n/total,
 *  actual-vs-projected bar, cost, net impact, ROAS. Clickable to the detail. */
function CampaignRow({
  row,
  currency,
  onOpen,
}: {
  row: CampaignListRow;
  currency: string;
  onOpen: () => void;
}) {
  const { campaign, actuals } = row;
  const matchedOf = actuals.matchedOrders; // n matched
  // The projected side of the bar is the modeled projected SALES, carried on the
  // actuals' variance block (the server composed it from the projection). Use it
  // when present, else the bar collapses (no honest projection to compare to).
  const projectedSales = actuals.variance?.projectedSales ?? null;

  return (
    <tr className="campaigns__row" onClick={onOpen}>
      <td>
        <div className="camp-name">
          <div>
            <div className="camp-name__nm">
              {campaign.name}
              {campaign.arabicName && (
                <span className="camp-name__ar" dir="rtl">
                  {campaign.arabicName}
                </span>
              )}
              {campaign.platformDealKey && (
                <span
                  className="camp-name__deal"
                  title="Platform deal name (as shown on the Talabat promotions screen)"
                >
                  {campaign.platformDealKey}
                </span>
              )}
            </div>
            <div className="camp-name__meta">{termsLabel(row)}</div>
          </div>
        </div>
      </td>
      <td>
        <SourceCell row={row} />
      </td>
      <td>
        <StatusBadge row={row} />
      </td>
      <td>
        {matchedOf > 0 ? (
          <span className="num">
            {num(matchedOf)}
            <span className="camp-name__meta"> / {num(expectedOf(row))}</span>
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        <ProjVsActualBar actual={actuals.sales} projected={projectedSales} />
      </td>
      <td className="num-col num">{actuals.cost ? money(actuals.cost, currency) : '—'}</td>
      <td className="num-col num">
        <NetImpact value={actuals.netMarginImpact} currency={currency} />
      </td>
      <td className="num-col">
        <RoasBadge roas={actuals.roas} meaningful={actuals.roasMeaningful} />
      </td>
      <td className="campaigns__chev" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="m9 6 6 6-6 6" />
        </svg>
      </td>
    </tr>
  );
}

/**
 * The source cell: one dot per source (a deduped campaign — `sources.length > 1`
 * — renders MULTIPLE dots) + the joined label ("Monthly + Platform") + the
 * participation tag. Monthly/platform discount campaigns are inferred from orders
 * ("participation detected"); ads/platform-API ones are known exactly ("from API").
 */
function SourceCell({ row }: { row: CampaignListRow }) {
  const { sources, provenance } = row.campaign;
  const label = sources.map((s) => SOURCE_LABEL[s]).join(' + ');
  const detected = provenance === 'admin';
  return (
    <span className="src-cell">
      <span className="src-cell__dots" aria-hidden="true">
        {sources.map((s) => (
          <span key={s} className={`src-dot src-${s}`} />
        ))}
      </span>
      <span className="src-cell__label">{label}</span>
      <span className={`src-tag ${detected ? 'src-tag--detected' : 'src-tag--api'}`}>
        {detected ? 'participation detected' : 'from API'}
      </span>
    </span>
  );
}

/** Status badge from the campaign's effective window vs the roll-up period. The
 *  list contract carries no explicit lifecycle, so derive it from validFrom/To. */
function StatusBadge({ row }: { row: CampaignListRow }) {
  const status = campaignStatus(row);
  const label = { active: 'Active', upcoming: 'Upcoming', ended: 'Ended' }[status];
  return <span className={`badge campaigns__status campaigns__status--${status}`}>{label}</span>;
}

/** The actual-vs-projected sales mini-bar: a muted projected track + a teal (good)
 *  or rose (bad) actual fill, with the actual/projected percentage. */
function ProjVsActualBar({
  actual,
  projected,
}: {
  actual: number;
  projected: number | null;
}) {
  if (!projected || projected <= 0) {
    return <span className="muted">—</span>;
  }
  const ratio = actual / projected;
  const good = actual >= projected;
  const pctLabel = Math.round(ratio * 100);
  const max = Math.max(actual, projected);
  const projW = (projected / max) * 100;
  const actW = Math.min(100, (actual / max) * 100);
  return (
    <div
      className="pva"
      role="img"
      aria-label={`Actual sales ${pctLabel}% of projected (${good ? 'at or above' : 'below'} projection)`}
    >
      <div className="pva__track">
        <div className="pva__proj" style={{ inlineSize: `${projW}%` }} />
        <div
          className={`pva__act ${good ? 'pva__act--good' : 'pva__act--bad'}`}
          style={{ inlineSize: `${actW}%` }}
        />
      </div>
      <span className="pva__pct num">{pct(pctLabel)}</span>
    </div>
  );
}

/** Net margin impact — green when positive, rose when negative. */
function NetImpact({ value, currency }: { value: number; currency: string }) {
  if (value === 0) return <span className="muted">—</span>;
  const cls = value > 0 ? 'campaigns__net--up' : 'campaigns__net--down';
  const sign = value > 0 ? '+' : '';
  return (
    <span className={cls}>
      {sign}
      {money(value, currency)}
    </span>
  );
}

/** The ROAS win/even/loss badge; "—" when ROAS is not meaningful (discount
 *  campaigns lead with net impact) or null. */
function RoasBadge({ roas, meaningful }: { roas: number | null; meaningful: boolean }) {
  if (roas === null || !meaningful) return <span className="muted">—</span>;
  const cls = roas >= 6 ? 'win' : roas >= 4 ? 'even' : 'loss';
  return <span className={`badge campaigns__roas campaigns__roas--${cls}`}>{num(round1(roas))}×</span>;
}

// ── Empty / loading / error states (the mock's state sampler, as real states) ──

function CampaignsEmpty({ storeName }: { storeName: string }) {
  return (
    <div className="campaigns">
      <div className="page-head">
        <h2>Campaigns</h2>
      </div>
      <div className="card empty" role="status">
        <IconCampaigns className="empty__glyph" aria-hidden="true" />
        <div className="empty__title">No campaigns matched yet</div>
        <p className="empty__sub">
          We pull {storeName}&rsquo;s ads and platform deals automatically, and your monthly
          offers are set by the Clarity team. Matching runs on the next data sync.
        </p>
      </div>
    </div>
  );
}

function CampaignsLoading() {
  return (
    <div className="campaigns">
      <div className="page-head">
        <h2>Campaigns</h2>
      </div>
      <div className="card campaigns__loading" role="status" aria-busy="true" aria-live="polite">
        <span className="sr-only">Matching campaigns to orders…</span>
        <span className="sk" style={{ inlineSize: '60%' }} aria-hidden="true" />
        <span className="sk" style={{ inlineSize: '90%' }} aria-hidden="true" />
        <span className="sk" style={{ inlineSize: '75%' }} aria-hidden="true" />
        <span className="sk" style={{ inlineSize: '85%' }} aria-hidden="true" />
      </div>
    </div>
  );
}

function CampaignsError() {
  return (
    <div className="campaigns">
      <div className="page-head">
        <h2>Campaigns</h2>
      </div>
      <p className="error" role="alert">
        Could not load campaigns for this branch.
      </p>
    </div>
  );
}

/**
 * The multi-branch prompt: campaigns are read per store, so an all/brand scope
 * spanning more than one branch asks the user to pick a branch (the per-store
 * detail is reached one branch at a time). Mirrors the branch-only PickBranch.
 */
function PickBranchForCampaigns({ branches }: { branches: StoreResponse[] }) {
  const { clientId } = useParams();
  const onboarded = branches.filter((b) => !isNotYetOnboarded(b));
  return (
    <div className="campaigns">
      <div className="page-head">
        <h2>Campaigns</h2>
        <p className="campaigns__lede">
          Campaigns are shown per branch. Choose a branch to see its campaigns.
        </p>
      </div>
      {onboarded.length === 0 ? (
        <p className="muted">No onboarded branches in scope yet.</p>
      ) : (
        <ul className="campaigns__pick" aria-label="Pick a branch">
          {onboarded.map((b) => (
            <li key={b.id}>
              <Link className="card campaigns__pick-item" to={`/c/${clientId}/store/${b.id}/campaigns`}>
                <IconCampaigns className="nav-ico" aria-hidden="true" />
                <span>{b.name ?? b.externalStoreId}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Small pure helpers ──────────────────────────────────────────────────────

function round0(n: number): number {
  return Math.round(n);
}
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** The matched-orders denominator. The list contract carries the matched count on
 *  `actuals`; the "of N" total is the campaign's expected orders where modeled
 *  (variance isn't a count), so fall back to the matched count itself. */
function expectedOf(row: CampaignListRow): number {
  // No separate expected-count field on the list row; show matched as its own
  // denominator ceiling so "n / n" reads honestly rather than inventing a total.
  return row.actuals.matchedOrders;
}

/** The row's terms label — the discount/cap or the ads pricing model. */
function termsLabel(row: CampaignListRow): string {
  const c = row.campaign;
  if (c.source === 'ads' || c.pricingModel) {
    const model = c.pricingModel ? c.pricingModel.toUpperCase() : 'ad';
    return c.cpc ? `${model} · ${num(c.cpc)} per click` : `${model} placement`;
  }
  const parts: string[] = [];
  const disc = discountRangeLabel(c);
  if (disc !== null) parts.push(`${disc} off`);
  if (c.maxDiscountCap !== null) parts.push(`cap ${num(c.maxDiscountCap)}`);
  if (c.audience !== 'all') parts.push(c.audience === 'plus' ? 'Plus only' : 'non-Plus');
  return parts.length > 0 ? parts.join(', ') : 'offer';
}

/** Derive the status from the campaign window vs now. */
function campaignStatus(row: CampaignListRow): 'active' | 'upcoming' | 'ended' {
  const now = Date.now();
  const from = new Date(row.campaign.validFrom).getTime();
  const to = row.campaign.validTo ? new Date(row.campaign.validTo).getTime() : null;
  if (!Number.isNaN(from) && from > now) return 'upcoming';
  if (to !== null && !Number.isNaN(to) && to < now) return 'ended';
  return 'active';
}
